import React, { useState, useEffect } from 'react';
import settingsManager from '../utils/settingsManager';

const getDefaultDistances = (coords, fallback = 30) => {
  if (!coords || coords.length < 2) return [];
  // Give each edge a slightly different default value so users can see they're independent
  return Array(coords.length - 1).fill(0).map((_, idx) => fallback + (idx * 2)); // 30, 32, 34, 36, etc.
};

const PCASettingsPanel = ({
  open,
  onClose,
  parcelCoords,
  setbackDistances,
  setSetbackDistances,
  onSetbacksChange,
  buildingPadding,
  setBuildingPadding,
  floorCount,
  setFloorCount,
  hasPodium,
  setHasPodium,
  podiumHeight,
  setPodiumHeight,
  // Building constraints
  minBoxesPerBuilding,
  setMinBoxesPerBuilding,
  maxBoxesPerBuilding,
  setMaxBoxesPerBuilding,
  // Box dimension props
  boxWidth,
  setBoxWidth,
  boxLength,
  setBoxLength,
  minBoxWidth,
  setMinBoxWidth,
  maxBoxWidth,
  setMaxBoxWidth,
  minBoxLength,
  setMinBoxLength,
  maxBoxLength,
  setMaxBoxLength,
  // Add new props for parcel boundaries
  showParcelBoundaries = false,
  onShowParcelBoundariesChange = () => {},
  regridApiToken = '',
  onRegridApiTokenChange = () => {},
  // Project address
  projectAddress = '',
  onProjectAddressChange = () => {},
  // Logout function
  onLogout = null
}) => {
  const [useIndividualSetbacks, setUseIndividualSetbacks] = useState(true);
  const [applyToAllValue, setApplyToAllValue] = useState('30');
  const [showSetbacks, setShowSetbacks] = useState(true);
  const [showBuildingFloors, setShowBuildingFloors] = useState(false);
  const [showBoxDimensions, setShowBoxDimensions] = useState(false);
  const [showParcelBoundariesSection, setShowParcelBoundariesSection] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Local state for input fields to prevent premature updates
  const [localBoxWidth, setLocalBoxWidth] = useState(boxWidth?.toString() || '');
  const [localBoxLength, setLocalBoxLength] = useState(boxLength?.toString() || '');
  const [localBuildingPadding, setLocalBuildingPadding] = useState(buildingPadding?.toString() || '');
  const [localMinBoxWidth, setLocalMinBoxWidth] = useState(minBoxWidth?.toString() || '');
  const [localMaxBoxWidth, setLocalMaxBoxWidth] = useState(maxBoxWidth?.toString() || '');
  const [localMinBoxLength, setLocalMinBoxLength] = useState(minBoxLength?.toString() || '');
  const [localMaxBoxLength, setLocalMaxBoxLength] = useState(maxBoxLength?.toString() || '');
  
  // Sync local state when props change
  useEffect(() => {
    setLocalBoxWidth(boxWidth?.toString() || '');
  }, [boxWidth]);
  
  useEffect(() => {
    setLocalBoxLength(boxLength?.toString() || '');
  }, [boxLength]);
  
  useEffect(() => {
    setLocalBuildingPadding(buildingPadding?.toString() || '');
  }, [buildingPadding]);
  
  useEffect(() => {
    setLocalMinBoxWidth(minBoxWidth?.toString() || '');
  }, [minBoxWidth]);
  
  useEffect(() => {
    setLocalMaxBoxWidth(maxBoxWidth?.toString() || '');
  }, [maxBoxWidth]);
  
  useEffect(() => {
    setLocalMinBoxLength(minBoxLength?.toString() || '');
  }, [minBoxLength]);
  
  useEffect(() => {
    setLocalMaxBoxLength(maxBoxLength?.toString() || '');
  }, [maxBoxLength]);
  
  // Advanced cost settings state
  const [componentsMin, setComponentsMin] = useState(settingsManager.getSetting('advanced.components.min'));
  const [componentsMax, setComponentsMax] = useState(settingsManager.getSetting('advanced.components.max'));
  const [onSiteMin, setOnSiteMin] = useState(settingsManager.getSetting('advanced.onSiteConstruction.min'));
  const [onSiteMax, setOnSiteMax] = useState(settingsManager.getSetting('advanced.onSiteConstruction.max'));

  // Helper function to apply numeric input values
  const applyNumericValue = (value, setter, min = 0) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= min) {
      setter(numValue);
    }
  };

  useEffect(() => {
    if (parcelCoords && parcelCoords.length > 1 && (!setbackDistances || setbackDistances.length !== parcelCoords.length - 1)) {
      // Initialize with individual values since we default to individual mode
      const newDistances = getDefaultDistances(parcelCoords, 30);
      setSetbackDistances(newDistances);
      // Set the "apply to all" value to the first edge's value for consistency
      setApplyToAllValue(String(newDistances[0] || 30));
      console.log('🔧 SETBACK INIT: Initialized individual setbacks:', newDistances);
    }
  }, [parcelCoords, setSetbackDistances]);

  // Save advanced settings when they change
  useEffect(() => {
    settingsManager.updateSetting('advanced.components.min', componentsMin);
  }, [componentsMin]);

  useEffect(() => {
    settingsManager.updateSetting('advanced.components.max', componentsMax);
  }, [componentsMax]);

  useEffect(() => {
    settingsManager.updateSetting('advanced.onSiteConstruction.min', onSiteMin);
  }, [onSiteMin]);

  useEffect(() => {
    settingsManager.updateSetting('advanced.onSiteConstruction.max', onSiteMax);
  }, [onSiteMax]);

  const handleToggleSetbackMode = (useIndividual) => {
    console.log('🔧 SETBACK MODE: Switching to', useIndividual ? 'individual' : 'apply to all');
    setUseIndividualSetbacks(useIndividual);
    
    if (!useIndividual) {
      // Switching TO "apply to all" mode - apply current value to all setbacks
      const currentValue = applyToAllValue || '30';
      setApplyToAllValue(currentValue);
      const newDistances = Array(setbackDistances.length).fill(Number(currentValue) || 30);
      console.log('🔧 SETBACK APPLY ALL: Setting all to', currentValue, '→', newDistances);
      setSetbackDistances(newDistances);
      onSetbacksChange && onSetbacksChange(newDistances);
    }
    // If switching to individual mode, keep existing values
  };

  const handleApplyToAllValueChange = (value) => {
    console.log('🔧 SETBACK APPLY ALL VALUE:', value);
    setApplyToAllValue(value);
    if (!useIndividualSetbacks) {
      // Only apply if we're in "apply to all" mode
      const num = Number(value) || 0;
      const newDistances = Array(setbackDistances.length).fill(num);
      console.log('🔧 SETBACK APPLY ALL: Setting all distances to', num);
      setSetbackDistances(newDistances);
      onSetbacksChange && onSetbacksChange(newDistances);
    }
  };

  const handleIndividualEdgeChange = (idx, value) => {
    console.log('🔧 SETBACK INDIVIDUAL: Edge', idx, 'set to', value);
    const num = Number(value) || 0;
    const newDistances = [...setbackDistances];
    newDistances[idx] = num;
    setSetbackDistances(newDistances);
    onSetbacksChange && onSetbacksChange(newDistances);
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: '420px',
      height: '100vh',
      background: 'white',
      borderLeft: '1px solid #E5E7EB',
      boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
      zIndex: 2000,
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'inherit',
      transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
      transform: open ? 'translateX(0)' : 'translateX(100%)'
    }}>
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: 'transparent',
          border: 'none',
          fontSize: '1.5rem',
          cursor: 'pointer',
          color: '#888',
          zIndex: 10
        }}
        aria-label="Close"
      >&times;</button>
      <div style={{
        padding: '2rem 2rem 1.5rem 2rem',
        overflowY: 'auto',
        flex: 1,
        minHeight: 0
      }}>
        {/* Setbacks Section with expand/collapse */}
        <div style={{ marginBottom: '2em' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setShowSetbacks(v => !v)}>
            <h3 style={{ fontSize: '1em', fontWeight: 600, marginBottom: 0 }}>Setbacks</h3>
            <span style={{ fontSize: '1.2em', color: '#888', userSelect: 'none' }}>{showSetbacks ? '▾' : '▸'}</span>
          </div>
          {showSetbacks && (
            <div style={{ marginTop: '1em' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="checkbox"
                  id="setIndividualSetbacksPCA"
                  checked={useIndividualSetbacks}
                  onChange={e => handleToggleSetbackMode(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label htmlFor="setIndividualSetbacksPCA" style={{ fontSize: '14px', cursor: 'pointer', fontWeight: '500' }}>
                  Individual setbacks for each edge
                </label>
              </div>
              
              {/* Individual setback controls - show when checkbox is checked (default) */}
              {useIndividualSetbacks && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {parcelCoords && parcelCoords.length > 1 && setbackDistances && setbackDistances.length === parcelCoords.length - 1 ? (
                    setbackDistances.map((distance, idx) => {
                      return (
                        <div key={idx}>
                          <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                            {`Edge ${idx + 1}`} <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(ft)</span>
                          </label>
                          <input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={distance || ''}
                            onChange={e => handleIndividualEdgeChange(idx, e.target.value)}
                            style={{ 
                              width: '100%', 
                              padding: '8px', 
                              border: '1px solid #E5E7EB', 
                              borderRadius: '4px', 
                              backgroundColor: 'white',
                              fontSize: '14px'
                            }}
                            placeholder={`Setback for edge ${idx + 1}`}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ color: '#888', fontSize: '14px' }}>Draw a parcel to set individual setbacks.</div>
                  )}
                </div>
              )}
              
              {/* Single input for all setbacks - show when checkbox is unchecked */}
              {!useIndividualSetbacks && (
                <div style={{ padding: '12px', backgroundColor: '#fff7ed', borderRadius: '4px', border: '1px solid #fed7aa' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span style={{ fontSize: '13px', color: '#ea580c', fontWeight: '500' }}>
                      Same distance applied to all edges
                    </span>
                  </div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>
                    Setback distance for all edges (ft)
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={applyToAllValue}
                    onChange={e => handleApplyToAllValueChange(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #E5E7EB', borderRadius: '4px', backgroundColor: 'white' }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {/* Building Spacing & Floors Section (with Podium moved here) */}
        <div style={{ marginBottom: '2em' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setShowBuildingFloors(v => !v)}>
            <h3 style={{ fontSize: '1em', fontWeight: 600, marginBottom: 0 }}>Building & Floors</h3>
            <span style={{ fontSize: '1.2em', color: '#888', userSelect: 'none' }}>{showBuildingFloors ? '▾' : '▸'}</span>
          </div>
          {showBuildingFloors && (
            <div style={{ marginTop: '1em' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Building Spacing (ft)</label>
                  <input
                    type="number"
                    value={localBuildingPadding}
                    min={0}
                    onChange={e => setLocalBuildingPadding(e.target.value)}
                    onBlur={e => applyNumericValue(e.target.value, setBuildingPadding, 0)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        applyNumericValue(e.target.value, setBuildingPadding, 0);
                        e.target.blur();
                      }
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #E5E7EB', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Floors per Building</label>
                  <input
                    type="number"
                    value={floorCount}
                    min={1}
                    onChange={e => setFloorCount(Number(e.target.value))}
                    style={{ width: '100%', padding: '8px', border: '1px solid #E5E7EB', borderRadius: '4px' }}
                  />
                </div>
                
                {/* Building Size Constraints */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Building Size Constraints</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Min Boxes</label>
                      <input
                        type="number"
                        value={minBoxesPerBuilding}
                        min={3}
                        max={maxBoxesPerBuilding || 50}
                        onChange={e => setMinBoxesPerBuilding(Number(e.target.value))}
                        style={{ 
                          width: '100%', 
                          padding: '6px', 
                          border: '1px solid #E5E7EB', 
                          borderRadius: '4px', 
                          fontSize: '13px' 
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Max Boxes</label>
                      <input
                        type="number"
                        value={maxBoxesPerBuilding}
                        min={minBoxesPerBuilding || 3}
                        max={50}
                        onChange={e => setMaxBoxesPerBuilding(Number(e.target.value))}
                        style={{ 
                          width: '100%', 
                          padding: '6px', 
                          border: '1px solid #E5E7EB', 
                          borderRadius: '4px', 
                          fontSize: '13px' 
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                    Optimized for maximum space utilization (3-30 boxes per building)
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  {/* Podium Toggle */}
                  <label htmlFor="hasPodium" style={{ fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: 0 }}>
                    <span>Enable Podium</span>
                    <span style={{ position: 'relative', display: 'inline-block', width: '38px', height: '22px' }}>
                      <input
                        id="hasPodium"
                        type="checkbox"
                        checked={!!hasPodium}
                        onChange={e => setHasPodium(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                      />
                      <span style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: hasPodium ? '#22c55e' : '#e5e7eb',
                        borderRadius: '22px',
                        transition: 'background 0.2s',
                        boxShadow: hasPodium ? '0 0 0 2px #22c55e33' : 'none'
                      }}></span>
                      <span style={{
                        position: 'absolute',
                        left: hasPodium ? '18px' : '2px',
                        top: '2px',
                        width: '18px',
                        height: '18px',
                        background: 'white',
                        borderRadius: '50%',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.12)'
                      }}></span>
                    </span>
                  </label>
                </div>
                {hasPodium && (
                  <div style={{ marginTop: '8px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Podium Height (ft)</label>
                    <input
                      type="number"
                      value={podiumHeight}
                      min={1}
                      onChange={e => setPodiumHeight(Number(e.target.value))}
                      style={{ width: '100%', padding: '8px', border: '1px solid #E5E7EB', borderRadius: '4px' }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Box Dimensions Section */}
        <div style={{ marginBottom: '2em' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setShowBoxDimensions(v => !v)}>
            <h3 style={{ fontSize: '1em', fontWeight: 600, marginBottom: 0 }}>Box Dimensions</h3>
            <span style={{ fontSize: '1.2em', color: '#888', userSelect: 'none' }}>{showBoxDimensions ? '▾' : '▸'}</span>
          </div>
          {showBoxDimensions && (
            <div style={{ marginTop: '1em' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Standard Dimensions */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Box Width (ft)</label>
                    <input
                      type="number"
                      value={localBoxWidth}
                      min={0}
                      step={0.1}
                      onChange={e => setLocalBoxWidth(e.target.value)}
                      onBlur={e => applyNumericValue(e.target.value, setBoxWidth, 0)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          applyNumericValue(e.target.value, setBoxWidth, 0);
                          e.target.blur();
                        }
                      }}
                      style={{ width: '100%', padding: '8px', border: '1px solid #E5E7EB', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Box Length (ft)</label>
                    <input
                      type="number"
                      value={localBoxLength}
                      min={0}
                      step={0.1}
                      onChange={e => setLocalBoxLength(e.target.value)}
                      onBlur={e => applyNumericValue(e.target.value, setBoxLength, 0)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          applyNumericValue(e.target.value, setBoxLength, 0);
                          e.target.blur();
                        }
                      }}
                      style={{ width: '100%', padding: '8px', border: '1px solid #E5E7EB', borderRadius: '4px' }}
                    />
                  </div>
                </div>
                
                {/* Width Constraints */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Width Constraints</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Min Width (ft)</label>
                      <input
                        type="number"
                        value={localMinBoxWidth}
                        min={0}
                        step={0.1}
                        onChange={e => setLocalMinBoxWidth(e.target.value)}
                        onBlur={e => applyNumericValue(e.target.value, setMinBoxWidth, 0)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            applyNumericValue(e.target.value, setMinBoxWidth, 0);
                            e.target.blur();
                          }
                        }}
                        style={{ width: '100%', padding: '6px', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '13px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Max Width (ft)</label>
                      <input
                        type="number"
                        value={localMaxBoxWidth}
                        min={0}
                        step={0.1}
                        onChange={e => setLocalMaxBoxWidth(e.target.value)}
                        onBlur={e => applyNumericValue(e.target.value, setMaxBoxWidth, 0)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            applyNumericValue(e.target.value, setMaxBoxWidth, 0);
                            e.target.blur();
                          }
                        }}
                        style={{ width: '100%', padding: '6px', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '13px' }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Length Constraints */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Length Constraints</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Min Length (ft)</label>
                      <input
                        type="number"
                        value={localMinBoxLength}
                        min={0}
                        step={0.1}
                        onChange={e => setLocalMinBoxLength(e.target.value)}
                        onBlur={e => applyNumericValue(e.target.value, setMinBoxLength, 0)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            applyNumericValue(e.target.value, setMinBoxLength, 0);
                            e.target.blur();
                          }
                        }}
                        style={{ width: '100%', padding: '6px', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '13px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Max Length (ft)</label>
                      <input
                        type="number"
                        value={localMaxBoxLength}
                        min={0}
                        step={0.1}
                        onChange={e => setLocalMaxBoxLength(e.target.value)}
                        onBlur={e => applyNumericValue(e.target.value, setMaxBoxLength, 0)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            applyNumericValue(e.target.value, setMaxBoxLength, 0);
                            e.target.blur();
                          }
                        }}
                        style={{ width: '100%', padding: '6px', border: '1px solid #E5E7EB', borderRadius: '4px', fontSize: '13px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Parcel Data Section */}
        <div style={{ marginBottom: '2em' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setShowParcelBoundariesSection(v => !v)}>
            <h3 style={{ fontSize: '1em', fontWeight: 600, marginBottom: 0 }}>
              Parcel Data
            </h3>
            <span style={{ fontSize: '1.2em', color: '#888', userSelect: 'none' }}>{showParcelBoundariesSection ? '▾' : '▸'}</span>
          </div>
          {showParcelBoundariesSection && (
            <div style={{ marginTop: '1em' }}>
              {/* Project Address Input */}
              <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '4px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Project Address
                </label>
                <input
                  type="text"
                  value={projectAddress || ''}
                  onChange={(e) => onProjectAddressChange(e.target.value)}
                  placeholder="Enter project address or use map search"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    textAlign: 'left'
                  }}
                />
                <div style={{ 
                  fontSize: '11px', 
                  color: '#6b7280', 
                  marginTop: '4px',
                  textAlign: 'left'
                }}>
                  Address is auto-filled when you search on the map
                </div>
              </div>

              {/* Parcel Boundaries Toggle */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={showParcelBoundaries}
                    onChange={(e) => onShowParcelBoundariesChange(e.target.checked)}
                    style={{ transform: 'scale(1.1)' }}
                  />
                  Show Parcel Boundaries for Tracing <span style={{ color: '#dc2626', fontSize: '0.8em', fontWeight: 500 }}>(BETA)</span>
                </label>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#6b7280', 
                  marginTop: '4px',
                  marginLeft: '24px' 
                }}>
                  Display parcel outlines to help trace property boundaries
                </div>
              </div>

              {/* Regrid API Token Input */}
              <div style={{ marginTop: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '4px', 
                  fontSize: '14px', 
                  fontWeight: '500' 
                }}>
                  Regrid API Token <span style={{ color: '#6b7280', fontWeight: 'normal' }}>(required)</span>
                </label>
                <input
                  type="text"
                  value={regridApiToken || "eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJyZWdyaWQuY29tIiwiaWF0IjoxNzUxNTQ4MjMwLCJleHAiOjE3NTQxNDAyMzAsInUiOjUzNDIxMSwiZyI6MjMxNTMsImNhcCI6InBhOnRzOnBzOmJmOm1hOnR5OmVvOnpvOnNiIn0.i5i1CU0URLx5NikC7H3_2YtrvS-O1NwJ_GIjXojobTA"}
                  onChange={(e) => onRegridApiTokenChange(e.target.value)}
                  placeholder="Enter your Regrid API token"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: showParcelBoundaries ? '#ffffff' : '#f9fafb',
                  }}
                  disabled={!showParcelBoundaries}
                />
                <div style={{ 
                  fontSize: '11px', 
                  color: '#6b7280', 
                  marginTop: '4px' 
                }}>
                  Get your token from <a 
                    href="https://regrid.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#3b82f6' }}
                  >
                    regrid.com
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Advanced Section with expand/collapse */}
        <div style={{ marginBottom: '2em', marginTop: '2em' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setShowAdvanced(v => !v)}>
            <h3 style={{ fontSize: '1em', fontWeight: 600, marginBottom: 0 }}>Advanced</h3>
            <span style={{ fontSize: '1.2em', color: '#888', userSelect: 'none' }}>{showAdvanced ? '▾' : '▸'}</span>
          </div>
          {showAdvanced && (
            <div style={{ marginTop: '1em' }}>
              {/* Components Cost Range */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Components</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Min ($)</label>
                    <input
                      type="number"
                      value={componentsMin}
                      min={0}
                      step={1000}
                      onChange={e => setComponentsMin(Number(e.target.value))}
                      style={{ 
                        width: '100%', 
                        padding: '8px', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Max ($)</label>
                    <input
                      type="number"
                      value={componentsMax}
                      min={0}
                      step={1000}
                      onChange={e => setComponentsMax(Number(e.target.value))}
                      style={{ 
                        width: '100%', 
                        padding: '8px', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="1000000"
                    />
                  </div>
                </div>
              </div>
              
              {/* On-Site Construction Cost Range */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>On-Site Construction</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Min ($)</label>
                    <input
                      type="number"
                      value={onSiteMin}
                      min={0}
                      step={1000}
                      onChange={e => setOnSiteMin(Number(e.target.value))}
                      style={{ 
                        width: '100%', 
                        padding: '8px', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#6b7280' }}>Max ($)</label>
                    <input
                      type="number"
                      value={onSiteMax}
                      min={0}
                      step={1000}
                      onChange={e => setOnSiteMax(Number(e.target.value))}
                      style={{ 
                        width: '100%', 
                        padding: '8px', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="1000000"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Refresh Analysis Button */}
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          <button
            onClick={() => {
              // Force re-calculation by updating a timestamp or triggering parent refresh
              if (window.location.reload) {
                // Simple approach: reload the page to ensure fresh calculation
                window.location.reload();
              } else {
                // Alternative: trigger a state change to force re-render
                console.log('Refreshing analysis with current settings...');
              }
            }}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 4v6h6M23 20v-6h-6"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
            </svg>
            Refresh Analysis
          </button>
        </div>

        {/* Logout Button */}
        {onLogout && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <button
              onClick={onLogout}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 17v4h-8v-4M19 10a9 9 0 0 0-9-9M5 10a9 9 0 0 0 9 9"/>
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PCASettingsPanel; 